
import { useState, useCallback, useEffect } from "react";
import { useOnboardingFetch } from "./useOnboardingFetch";
import { useOnboardingCredentials } from "./useOnboardingCredentials";
import { useOnboardingTransitions } from "./useOnboardingTransitions";
import { OnboardingState } from "./types";

// Patch updater for onboarding state table
function usePatch(onboarding: OnboardingState | null, setOnboarding: (o: OnboardingState | null) => void) {
  return useCallback(
    async (updates: Partial<OnboardingState>) => {
      if (!onboarding) return false;
      try {
        const { supabase } = await import("@/integrations/supabase/client");
        const { error, data } = await supabase
          .from("calie_onboarding_state")
          .update({ ...updates, updated_at: new Date().toISOString() })
          .eq("tenant_id", onboarding.tenant_id)
          .select("*")
          .maybeSingle();

        if (error) {
          return false;
        }
        if (data) {
          // Always optimistically set latest onboarding state, but don't overwrite tokens
          const updatedState: OnboardingState = {
            ...onboarding,
            ...data,
            twilio_sid: onboarding.twilio_sid,
            twilio_auth_token: onboarding.twilio_auth_token,
            status: data.onboarding_completed_at ? "completed" : "in_progress" as "completed" | "in_progress",
          };
          setOnboarding(updatedState);
        }
        return !error;
      } catch (err) {
        return false;
      }
    },
    [onboarding, setOnboarding]
  );
}

export function useOnboardingState() {
  const { onboarding, setOnboarding, loading, error } = useOnboardingFetch();
  const patch = usePatch(onboarding, setOnboarding);

  // Data integrity check
  useEffect(() => {
    if (!loading && onboarding && onboarding.status === "completed") {
      const checkDataIntegrity = async () => {
        const { supabase } = await import("@/integrations/supabase/client");
        
        // Fetch Twilio SID from tenants table
        const { data: tenantData, error: tenantError } = await supabase
          .from("calie_tenants")
          .select("twilio_sid")
          .eq("tenant_id", onboarding.tenant_id)
          .maybeSingle();

        // Fetch forwarding number from onboarding state (redundant but for explicit check)
        const { data: onboardingData, error: onboardingError } = await supabase
          .from("calie_onboarding_state")
          .select("forwarding_number")
          .eq("tenant_id", onboarding.tenant_id)
          .maybeSingle();

        if (tenantError || onboardingError) {
          console.error("Error checking onboarding data integrity:", tenantError || onboardingError);
          return;
        }

        const hasTwilioSid = !!tenantData?.twilio_sid;
        const hasForwardingNumber = !!onboardingData?.forwarding_number;

        if (!hasTwilioSid || !hasForwardingNumber) {
          console.warn("Onboarding status is 'completed' but essential data is missing. Resetting status to 'in_progress'.");
          setOnboarding({
            ...onboarding,
            status: "in_progress",
            onboarding_completed_at: null, // Clear completion timestamp
          });
        }
      };
      checkDataIntegrity();
    }
  }, [loading, onboarding, setOnboarding]);

  const { updateTwilio, validateTwilioCredentials, syncTwilioNumbers } = useOnboardingCredentials(onboarding, setOnboarding, patch);
  const { buyNumber, saveForwarding, saveRecordingPrefs, markComplete } = useOnboardingTransitions(onboarding, patch);

  const resetOnboarding = useCallback(async () => {
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data, error: fnError } = await supabase.functions.invoke("Calie_reset_onboarding", {
        body: {},
      });
      if (fnError || (data && data.success === false)) {
        return false;
      }
      if (onboarding) {
        setOnboarding({
          ...onboarding,
          current_step: "twilio",
          onboarding_completed_at: undefined,
          first_number_added_at: null,
          twilio_connected_at: null,
          forwarding_number: null,
          recording_enabled: true,
          retention_days: 30,
          dni_snippet_generated: false,
          status: "in_progress",
          updated_at: new Date().toISOString(),
        });
      }
      return true;
    } catch (e) {
      return false;
    }
  }, [onboarding, setOnboarding]);

  return {
    onboarding,
    loading,
    error,
    updateTwilio,
    buyNumber,
    saveForwarding,
    saveRecordingPrefs,
    markComplete,
    validateTwilioCredentials,
    syncTwilioNumbers,
    resetOnboarding,
  };
}
